/**
 * /admin/$slug/create-booking — Feature 6.
 *
 * Owner/admin-only wizard for creating a booking on behalf of a client.
 * Adapted from the public /book/$slug/$serviceId page but with three
 * key differences:
 *
 *   1. Picks the client first (search-existing OR create-new) before
 *      anything else. The public flow assumes the client IS the user.
 *   2. Shows internal-only services (bookable_by_client = false) with
 *      an "Internal only" chip — admins can book those, that's the
 *      whole point of the flag.
 *   3. Skips Turnstile, skips the approval queue, and offers explicit
 *      per-channel notification toggles in the Confirm step.
 *
 * Wizard step navigation is page-local (`useState<Step>`) rather than
 * separate routes — keeps the picked client + picked service in memory
 * across steps without round-tripping through the URL, and matches the
 * single-page pattern used on the public booking page. The ?clientId
 * query param is a one-shot pre-fill (e.g. when launched via the
 * "Create follow-up" button on BookingMoveDialog), consumed once on
 * mount.
 */
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Calendar,
  Card,
  FormField,
  Input,
  SegmentedControl,
  TimeSlotPicker,
  Textarea,
  type TimeSlot,
} from '@bookaburra/ui';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Lock,
  LogIn,
  Search,
  UserPlus,
  XCircle,
} from 'lucide-react';
import { fetchBookingTenant, fetchSlots } from '../booking-flow/api';
import { fetchAdminServices } from '../booking-flow/translations-api';
import { fetchMyMemberships } from '../booking-flow/tenant-api';
import {
  AdminCreateBookingApiError,
  type AdminCreateBookingError,
  type ClientSearchHit,
  createBookingForClient,
  createClient,
  searchClientsByLastName,
} from '../booking-flow/admin-create-booking-api';
import { useDocumentTitle } from '../lib/use-document-title';

type Step = 'client' | 'service' | 'slot' | 'confirm' | 'done';

interface CreateBookingSearch {
  /** Optional client to pre-select on mount — set when the admin clicks
   *  "Create follow-up for X" from BookingMoveDialog. */
  clientId?: string;
}

export const Route = createFileRoute('/admin/$slug/create-booking')({
  validateSearch: (search: Record<string, unknown>): CreateBookingSearch => ({
    clientId: typeof search.clientId === 'string' ? search.clientId : undefined,
  }),
  component: CreateBookingPage,
});

function CreateBookingPage() {
  const { slug } = Route.useParams();
  const { clientId: prefillClientId } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Role gate (client-side mirror of the server check). Regular staff
  // get bounced to my-day with a one-liner. The server-side gate in
  // /api/admin/:slug/create-booking is authoritative; this is just so
  // staff don't see the half-loaded form before the redirect fires.
  const meQ = useQuery({
    queryKey: ['me-memberships'],
    queryFn: fetchMyMemberships,
    staleTime: 60_000,
  });
  const role = meQ.data?.memberships.find((m) => m.tenant.slug === slug)?.role;
  const denied = meQ.isFetched && role !== 'owner' && role !== 'manager';
  useEffect(() => {
    if (denied) {
      navigate({
        to: '/admin/$slug/my-day',
        params: { slug },
        replace: true,
      });
    }
  }, [denied, navigate, slug]);

  const [step, setStep] = useState<Step>('client');
  const [selectedClient, setSelectedClient] = useState<ClientSearchHit | null>(null);
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);
  const [date, setDate] = useState<Date | null>(null);
  const [selectedSlotStart, setSelectedSlotStart] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(false);
  const [createdBookingId, setCreatedBookingId] = useState<string | null>(null);
  const [submitErr, setSubmitErr] = useState<AdminCreateBookingError | null>(null);

  // One-shot prefill: if launched with ?clientId, fetch the client and
  // jump straight to the service step.
  useEffect(() => {
    if (!prefillClientId || selectedClient) return;
    // The clients/search endpoint is keyed by last-name prefix; for a
    // direct id pre-fill we'd rather hit a `/clients/:id` lookup, but
    // we don't have one and don't want to add another endpoint for the
    // one-shot prefill case. Cheap workaround: skip the lookup and let
    // the wizard render a "Pre-filled client" placeholder. The booking
    // POST sends the id straight to the server, so we just need a
    // display label — and we can pull the name from the first
    // services-page hit. For now, store a minimal placeholder so the
    // wizard renders.
    setSelectedClient({
      id: prefillClientId,
      name: 'Selected client',
      firstName: null,
      lastName: null,
      phone: null,
      email: null,
      lastBookingDate: null,
      lastBookingService: null,
    });
    setStep('service');
  }, [prefillClientId, selectedClient]);

  const tenantQ = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  useDocumentTitle(
    tenantQ.data ? `Create booking · ${tenantQ.data.tenant.name} Admin` : 'Create booking',
  );
  const timezone = tenantQ.data?.tenant.timezone ?? 'Australia/Sydney';

  // Admin services endpoint returns everything including internal-only
  // services + archived ones. We filter out archived (not bookable
  // anywhere) but keep internal-only (the whole point of admin
  // booking creation).
  const servicesQ = useQuery({
    enabled: step === 'service' || step === 'slot' || step === 'confirm',
    queryKey: ['admin-services', slug],
    queryFn: () => fetchAdminServices(slug),
    staleTime: 60_000,
  });
  const services = useMemo(
    () => (servicesQ.data?.services ?? []).filter((s) => s.archivedAt === null),
    [servicesQ.data],
  );
  const selectedService = useMemo(
    () => services.find((s) => s.id === selectedServiceId) ?? null,
    [services, selectedServiceId],
  );

  const dateStr = useMemo(() => (date ? toLocalDateString(date) : null), [date]);
  const slotsQ = useQuery({
    enabled: Boolean(dateStr && selectedServiceId && step === 'slot'),
    queryKey: ['admin-create-slots', slug, selectedServiceId, dateStr],
    queryFn: () => fetchSlots(slug, selectedServiceId!, dateStr!),
  });

  const submitMut = useMutation({
    mutationFn: () =>
      createBookingForClient(slug, {
        clientId: selectedClient!.id,
        serviceId: selectedServiceId!,
        slotStart: selectedSlotStart!,
        sendEmail,
        sendSms,
        notes: notes.trim() || undefined,
      }),
    onSuccess: (data) => {
      setCreatedBookingId(data.booking.id);
      setStep('done');
      // Refresh both calendar + my-day so the new booking shows up
      // immediately if the admin navigates back.
      qc.invalidateQueries({ queryKey: ['admin-calendar', slug] });
      qc.invalidateQueries({ queryKey: ['my-day', slug] });
    },
    onError: (err: Error & AdminCreateBookingError) => setSubmitErr(err),
  });

  if (meQ.error instanceof Error && !meQ.data) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-10 sm:px-12">
        <SignInPrompt slug={slug} />
      </main>
    );
  }

  // While memberships load (or we've decided to redirect), render a
  // minimal placeholder so we don't flash form chrome.
  if (!meQ.isFetched || denied) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-10 sm:px-12">
        <div className="h-24 animate-pulse rounded-2xl bg-brand-cream-shadow" />
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-3xl px-6 py-10 sm:px-12">
      <header className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">Create a booking</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Book on behalf of a client. Notifications are off by default for
          SMS &mdash; you control them on the last step.
        </p>
      </header>

      <Stepper step={step} />

      {step === 'client' ? (
        <ClientStep
          slug={slug}
          onPick={(c) => {
            setSelectedClient(c);
            setStep('service');
          }}
        />
      ) : null}

      {step === 'service' ? (
        <ServiceStep
          loading={servicesQ.isLoading}
          services={services}
          onPick={(id) => {
            setSelectedServiceId(id);
            setDate(null);
            setSelectedSlotStart(null);
            setStep('slot');
          }}
          onBack={() => setStep('client')}
        />
      ) : null}

      {step === 'slot' && selectedService ? (
        <SlotStep
          serviceName={selectedService.name}
          timezone={timezone}
          date={date}
          slots={
            slotsQ.data?.slots.map((s) => ({ start: s.start, label: undefined })) ?? []
          }
          slotsLoading={slotsQ.isLoading}
          onDateChange={(d) => {
            setDate(d);
            setSelectedSlotStart(null);
          }}
          onSlotChange={(s) => setSelectedSlotStart(s.start)}
          selectedStart={selectedSlotStart}
          onBack={() => setStep('service')}
          onContinue={() => setStep('confirm')}
        />
      ) : null}

      {step === 'confirm' && selectedClient && selectedService && selectedSlotStart ? (
        <ConfirmStep
          tenantName={tenantQ.data?.tenant.name ?? slug}
          client={selectedClient}
          service={{
            id: selectedService.id,
            name: selectedService.name,
            durationMinutes: selectedService.durationMinutes,
            bookableByClient: selectedService.bookableByClient,
          }}
          slotStart={selectedSlotStart}
          timezone={timezone}
          notes={notes}
          onNotesChange={setNotes}
          sendEmail={sendEmail}
          onSendEmailChange={setSendEmail}
          sendSms={sendSms}
          onSendSmsChange={setSendSms}
          submitting={submitMut.isPending}
          submitErr={submitErr}
          onBack={() => setStep('slot')}
          onSubmit={() => {
            setSubmitErr(null);
            submitMut.mutate();
          }}
        />
      ) : null}

      {step === 'done' && createdBookingId && selectedClient ? (
        <DoneStep
          slug={slug}
          bookingId={createdBookingId}
          clientName={selectedClient.name}
          onCreateAnother={() => {
            setSelectedClient(null);
            setSelectedServiceId(null);
            setDate(null);
            setSelectedSlotStart(null);
            setNotes('');
            setSendEmail(true);
            setSendSms(false);
            setCreatedBookingId(null);
            setSubmitErr(null);
            setStep('client');
          }}
        />
      ) : null}
    </main>
  );
}

/* ─────────────────────────── stepper ────────────────────────────── */

const STEP_LABELS: Record<Step, string> = {
  client: 'Client',
  service: 'Service',
  slot: 'Date & time',
  confirm: 'Confirm',
  done: 'Done',
};

const STEP_ORDER: Step[] = ['client', 'service', 'slot', 'confirm', 'done'];

function Stepper({ step }: { step: Step }) {
  const currentIdx = STEP_ORDER.indexOf(step);
  return (
    <ol
      role="list"
      aria-label="Booking wizard progress"
      className="mb-8 flex flex-wrap items-center gap-2 text-xs"
    >
      {STEP_ORDER.map((s, i) => {
        const isCurrent = i === currentIdx;
        const isPast = i < currentIdx;
        return (
          <li key={s} className="flex items-center gap-2">
            <span
              className={
                isCurrent
                  ? 'inline-flex items-center gap-1 rounded-full bg-brand-sage px-2.5 py-0.5 font-semibold uppercase tracking-widest text-brand-cream'
                  : isPast
                    ? 'inline-flex items-center gap-1 rounded-full bg-brand-sage/20 px-2.5 py-0.5 font-semibold uppercase tracking-widest text-brand-sage'
                    : 'inline-flex items-center gap-1 rounded-full border border-brand-sage/30 px-2.5 py-0.5 font-semibold uppercase tracking-widest text-brand-sage/50'
              }
            >
              {i + 1}. {STEP_LABELS[s]}
            </span>
            {i < STEP_ORDER.length - 1 ? (
              <ChevronRight className="h-3 w-3 text-brand-sage/40" />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

/* ─────────────────────────── client step ────────────────────────── */

function ClientStep({
  slug,
  onPick,
}: {
  slug: string;
  onPick: (client: ClientSearchHit) => void;
}) {
  const [mode, setMode] = useState<'search' | 'new'>('search');
  return (
    <section className="space-y-6">
      <SegmentedControl
        value={mode}
        onChange={setMode}
        ariaLabel="Client mode"
        options={[
          {
            value: 'search',
            label: 'Search existing',
            icon: <Search className="h-3.5 w-3.5" />,
          },
          {
            value: 'new',
            label: 'Create new',
            icon: <UserPlus className="h-3.5 w-3.5" />,
          },
        ]}
      />
      {mode === 'search' ? (
        <SearchExistingPanel slug={slug} onPick={onPick} />
      ) : (
        <CreateNewPanel slug={slug} onCreated={onPick} />
      )}
    </section>
  );
}

function SearchExistingPanel({
  slug,
  onPick,
}: {
  slug: string;
  onPick: (client: ClientSearchHit) => void;
}) {
  const [query, setQuery] = useState('');
  // Autocomplete fires only at >= 3 chars (per spec). Debounce isn't
  // strictly necessary — react-query dedupes by key — but a touch of
  // visual delay avoids "results jumping under your fingers" on fast
  // typers.
  const trimmed = query.trim();
  const enabled = trimmed.length >= 3;
  const searchQ = useQuery({
    enabled,
    queryKey: ['admin-clients-search', slug, trimmed.toLowerCase()],
    queryFn: () => searchClientsByLastName(slug, trimmed),
    staleTime: 30_000,
  });

  return (
    <Card className="border-brand-sage/15 p-6 shadow-brand">
      <FormField
        label="Search by last name"
        description="Type at least 3 letters. 'McGra' matches both McGrath and Mc Grath."
      >
        {({ id }) => (
          <Input
            id={id}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. Smith"
            autoComplete="off"
          />
        )}
      </FormField>

      {!enabled ? (
        <p className="mt-4 text-xs text-muted-foreground">
          Showing matches once you&rsquo;ve typed 3+ letters.
        </p>
      ) : searchQ.isLoading ? (
        <p className="mt-4 text-xs text-muted-foreground">Searching…</p>
      ) : searchQ.error ? (
        <Alert variant="destructive" className="mt-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Could not search</AlertTitle>
          <AlertDescription>{(searchQ.error as Error).message}</AlertDescription>
        </Alert>
      ) : !searchQ.data || searchQ.data.clients.length === 0 ? (
        <p className="mt-4 text-xs text-muted-foreground">
          No matches. Try &ldquo;Create new&rdquo; instead.
        </p>
      ) : (
        <ul className="mt-4 divide-y divide-brand-sage/10 overflow-hidden rounded-lg border border-brand-sage/15">
          {searchQ.data.clients.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => onPick(c)}
                className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-brand-cream-shadow/40 focus-visible:bg-brand-cream-shadow/60 focus-visible:outline-none"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground">{c.name}</p>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                    {[c.phone ? formatPhone(c.phone) : null, c.email]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {c.lastBookingDate
                    ? `Last booked: ${formatShortDate(c.lastBookingDate)}`
                    : 'No bookings yet'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function CreateNewPanel({
  slug,
  onCreated,
}: {
  slug: string;
  onCreated: (client: ClientSearchHit) => void;
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [locality, setLocality] = useState('');
  const [postcode, setPostcode] = useState('');
  const [region, setRegion] = useState('');
  const [country, setCountry] = useState('AU');
  const [err, setErr] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () =>
      createClient(slug, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        addressLine1: addressLine1.trim() || undefined,
        locality: locality.trim() || undefined,
        postcode: postcode.trim() || undefined,
        region: region.trim() || undefined,
        country: country.trim() || undefined,
      }),
    onSuccess: (data) => {
      // Map server shape to ClientSearchHit so the wizard's selected
      // client carries the same fields as a search result.
      onCreated({
        id: data.client.id,
        name: data.client.name,
        firstName: data.client.firstName,
        lastName: data.client.lastName,
        phone: data.client.phone,
        email: data.client.email,
        lastBookingDate: null,
        lastBookingService: null,
      });
    },
    onError: (e: Error) => setErr(e.message),
  });

  const validEmail = !email || /.+@.+\..+/.test(email);
  const validPhone = !phone || /[0-9+ -]{7,}/.test(phone);
  const hasContact = email.trim() || phone.trim();
  const canSubmit =
    (firstName.trim() || lastName.trim()) &&
    hasContact &&
    validEmail &&
    validPhone &&
    !createMut.isPending;

  return (
    <Card className="border-brand-sage/15 p-6 shadow-brand">
      <form
        className="grid gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          setErr(null);
          createMut.mutate();
        }}
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <FormField label="First name">
            {({ id }) => (
              <Input
                id={id}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Sarah"
                autoComplete="given-name"
              />
            )}
          </FormField>
          <FormField label="Last name">
            {({ id }) => (
              <Input
                id={id}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Mitchell"
                autoComplete="family-name"
              />
            )}
          </FormField>
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <FormField
            label="Email"
            description="Email or phone required."
            error={email && !validEmail ? 'Enter a valid email.' : undefined}
          >
            {({ id, invalid, describedBy }) => (
              <Input
                id={id}
                type="email"
                invalid={invalid}
                aria-describedby={describedBy}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="sarah@example.com.au"
                autoComplete="email"
              />
            )}
          </FormField>
          <FormField
            label="Mobile"
            error={phone && !validPhone ? 'Enter a valid phone.' : undefined}
          >
            {({ id, invalid, describedBy }) => (
              <Input
                id={id}
                type="tel"
                invalid={invalid}
                aria-describedby={describedBy}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="0400 000 000"
                autoComplete="tel"
              />
            )}
          </FormField>
        </div>
        <FormField label="Address line 1" description="Optional.">
          {({ id }) => (
            <Input
              id={id}
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              placeholder="12 Smith Street"
              autoComplete="address-line1"
            />
          )}
        </FormField>
        <div className="grid gap-5 sm:grid-cols-2">
          <FormField label="Suburb / locality">
            {({ id }) => (
              <Input
                id={id}
                value={locality}
                onChange={(e) => setLocality(e.target.value)}
                placeholder="Newtown"
                autoComplete="address-level2"
              />
            )}
          </FormField>
          <FormField label="Postcode">
            {({ id }) => (
              <Input
                id={id}
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                placeholder="2042"
                autoComplete="postal-code"
              />
            )}
          </FormField>
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <FormField label="State / region">
            {({ id }) => (
              <Input
                id={id}
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder="NSW"
                autoComplete="address-level1"
              />
            )}
          </FormField>
          <FormField label="Country">
            {({ id }) => (
              <Input
                id={id}
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="AU"
                autoComplete="country"
                maxLength={2}
              />
            )}
          </FormField>
        </div>
        {err ? (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertTitle>Could not create client</AlertTitle>
            <AlertDescription>{err}</AlertDescription>
          </Alert>
        ) : null}
        <div className="flex justify-end">
          <Button type="submit" disabled={!canSubmit} loading={createMut.isPending}>
            Create client &amp; continue
          </Button>
        </div>
      </form>
    </Card>
  );
}

/* ─────────────────────────── service step ───────────────────────── */

function ServiceStep({
  loading,
  services,
  onPick,
  onBack,
}: {
  loading: boolean;
  services: { id: string; name: string; durationMinutes: number; bookableByClient: boolean }[];
  onPick: (id: string) => void;
  onBack: () => void;
}) {
  if (loading) {
    return (
      <Card className="space-y-3 border-brand-sage/15 p-6 shadow-brand">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-12 animate-pulse rounded-md bg-brand-cream-shadow" />
        ))}
      </Card>
    );
  }
  if (services.length === 0) {
    return (
      <Card className="border-brand-sage/15 p-6 shadow-brand">
        <p className="text-sm text-muted-foreground">
          No services yet. Add one in{' '}
          <span className="font-medium text-foreground">Services</span> before
          creating a booking.
        </p>
      </Card>
    );
  }
  return (
    <Card className="border-brand-sage/15 p-2 shadow-brand">
      <ul className="divide-y divide-brand-sage/10">
        {services.map((s) => (
          <li key={s.id}>
            <button
              type="button"
              onClick={() => onPick(s.id)}
              className="flex w-full items-center justify-between gap-3 rounded-lg px-4 py-3 text-left transition-colors hover:bg-brand-cream-shadow/40 focus-visible:bg-brand-cream-shadow/60 focus-visible:outline-none"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-medium text-foreground">{s.name}</p>
                  {!s.bookableByClient ? (
                    <Badge variant="secondary" className="text-[10px]">
                      Internal only
                    </Badge>
                  ) : null}
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {s.durationMinutes} minutes
                </p>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          </li>
        ))}
      </ul>
      <div className="flex justify-start p-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          ← Back to client
        </Button>
      </div>
    </Card>
  );
}

/* ─────────────────────────── slot step ──────────────────────────── */

function SlotStep({
  serviceName,
  timezone,
  date,
  slots,
  slotsLoading,
  selectedStart,
  onDateChange,
  onSlotChange,
  onBack,
  onContinue,
}: {
  serviceName: string;
  timezone: string;
  date: Date | null;
  slots: TimeSlot[];
  slotsLoading: boolean;
  selectedStart: number | null;
  onDateChange: (d: Date) => void;
  onSlotChange: (s: TimeSlot) => void;
  onBack: () => void;
  onContinue: () => void;
}) {
  return (
    <section className="space-y-6">
      <Card className="border-brand-sage/15 p-6 shadow-brand">
        <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {serviceName}
        </p>
        <div className="grid gap-8 lg:grid-cols-[auto_1fr]">
          <div>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-widest text-muted-foreground">
              Pick a day
            </h2>
            <Calendar
              value={date}
              onChange={onDateChange}
              minDate={new Date()}
            />
          </div>
          <div>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-widest text-muted-foreground">
              Pick a time
            </h2>
            {!date ? (
              <p className="rounded-md border border-dashed border-border bg-secondary/50 p-6 text-sm text-muted-foreground">
                Choose a day on the left.
              </p>
            ) : slotsLoading ? (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-12 animate-pulse rounded-md bg-brand-cream-shadow" />
                ))}
              </div>
            ) : (
              <TimeSlotPicker
                slots={slots}
                value={selectedStart}
                onSelect={onSlotChange}
                timezone={timezone}
                emptyMessage="No times available on this day."
              />
            )}
          </div>
        </div>
      </Card>
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onBack}>
          ← Back to service
        </Button>
        <Button
          disabled={selectedStart === null}
          onClick={onContinue}
        >
          Continue
        </Button>
      </div>
    </section>
  );
}

/* ─────────────────────────── confirm step ───────────────────────── */

function ConfirmStep({
  tenantName,
  client,
  service,
  slotStart,
  timezone,
  notes,
  onNotesChange,
  sendEmail,
  onSendEmailChange,
  sendSms,
  onSendSmsChange,
  submitting,
  submitErr,
  onBack,
  onSubmit,
}: {
  tenantName: string;
  client: ClientSearchHit;
  service: { id: string; name: string; durationMinutes: number; bookableByClient: boolean };
  slotStart: number;
  timezone: string;
  notes: string;
  onNotesChange: (v: string) => void;
  sendEmail: boolean;
  onSendEmailChange: (v: boolean) => void;
  sendSms: boolean;
  onSendSmsChange: (v: boolean) => void;
  submitting: boolean;
  submitErr: AdminCreateBookingError | null;
  onBack: () => void;
  onSubmit: () => void;
}) {
  const startLabel = formatLongDateTime(slotStart, timezone);
  return (
    <Card className="space-y-6 border-brand-sage/15 p-6 shadow-brand">
      <h2 className="text-lg font-semibold">Confirm</h2>
      <dl className="grid gap-3 text-sm">
        <Row label="Business" value={tenantName} />
        <Row label="Client" value={client.name} />
        <Row
          label="Contact"
          value={
            [client.phone ? formatPhone(client.phone) : null, client.email]
              .filter(Boolean)
              .join(' · ') || '—'
          }
        />
        <Row
          label="Service"
          value={
            <span className="inline-flex flex-wrap items-center gap-2">
              {service.name} <span className="text-muted-foreground">· {service.durationMinutes} min</span>
              {!service.bookableByClient ? (
                <Badge variant="secondary" className="text-[10px]">
                  Internal only
                </Badge>
              ) : null}
            </span>
          }
        />
        <Row label="When" value={startLabel} />
      </dl>

      <FormField label="Notes for the team" description="Optional.">
        {({ id }) => (
          <Textarea
            id={id}
            rows={3}
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            maxLength={500}
          />
        )}
      </FormField>

      <fieldset className="grid gap-2 rounded-lg border border-brand-sage/15 bg-secondary/30 p-4 text-sm">
        <legend className="px-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Notifications
        </legend>
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={sendEmail}
            onChange={(e) => onSendEmailChange(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-input"
            disabled={!client.email}
          />
          <span>
            <span className="font-medium">Send confirmation email to client</span>
            {!client.email ? (
              <span className="block text-xs text-muted-foreground">
                No email on file.
              </span>
            ) : (
              <span className="block text-xs text-muted-foreground">
                Goes to {client.email}.
              </span>
            )}
          </span>
        </label>
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={sendSms}
            onChange={(e) => onSendSmsChange(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-input"
            disabled={!client.phone}
          />
          <span>
            <span className="font-medium">Send confirmation SMS to client</span>
            {!client.phone ? (
              <span className="block text-xs text-muted-foreground">
                No mobile on file.
              </span>
            ) : (
              <span className="block text-xs text-muted-foreground">
                Off by default to avoid surprises. Goes to{' '}
                {formatPhone(client.phone)}.
              </span>
            )}
          </span>
        </label>
      </fieldset>

      {submitErr ? (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertTitle>
            {submitErr.error === 'slot_unavailable'
              ? 'That slot is no longer available'
              : 'Could not create booking'}
          </AlertTitle>
          <AlertDescription>
            {submitErr.error === 'slot_unavailable'
              ? 'Go back and pick another time.'
              : submitErr.error}
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onBack}>
          ← Back to date &amp; time
        </Button>
        <Button onClick={onSubmit} loading={submitting} disabled={submitting}>
          Create booking
        </Button>
      </div>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-3">
      <dt className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm text-foreground">{value}</dd>
    </div>
  );
}

/* ─────────────────────────── done step ──────────────────────────── */

function DoneStep({
  slug,
  bookingId,
  clientName,
  onCreateAnother,
}: {
  slug: string;
  bookingId: string;
  clientName: string;
  onCreateAnother: () => void;
}) {
  return (
    <Card className="space-y-5 border-brand-sage/15 p-6 shadow-brand">
      <Alert variant="success">
        <CheckCircle2 className="h-4 w-4" />
        <AlertTitle>Booking created</AlertTitle>
        <AlertDescription>
          Confirmed for {clientName}. Booking id <code>{bookingId}</code>.
        </AlertDescription>
      </Alert>
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/admin/$slug" params={{ slug }}>
          <Button>Back to calendar</Button>
        </Link>
        <Button variant="outline" onClick={onCreateAnother}>
          Create another
        </Button>
      </div>
    </Card>
  );
}

/* ─────────────────────────── sign-in ───────────────────────────── */

function SignInPrompt({ slug }: { slug: string }) {
  return (
    <Card className="mx-auto max-w-md p-8 text-center">
      <Lock className="mx-auto h-8 w-8 text-muted-foreground" />
      <h2 className="mt-4 text-lg font-semibold">Sign in to continue</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Creating bookings is only available to signed-in admins.
      </p>
      <div className="mt-6">
        <Link to="/signin" search={{ redirect: `/admin/${slug}/create-booking` }}>
          <Button leadingIcon={<LogIn className="h-4 w-4" />}>Sign in</Button>
        </Link>
      </div>
    </Card>
  );
}

/* ─────────────────────────── helpers ───────────────────────────── */

function toLocalDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatShortDate(epochMs: number): string {
  return new Date(epochMs).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatLongDateTime(epochMs: number, timezone: string): string {
  const date = new Date(epochMs).toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: timezone,
  });
  const time = new Date(epochMs).toLocaleTimeString('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone,
  });
  return `${date} at ${time}`;
}

/**
 * Light-touch phone formatter — splits an AU mobile into 4-3-3 groups
 * (0400 000 000) when it parses cleanly, falls back to the raw string
 * otherwise. The server stores the canonical E.164 form; this is
 * display only.
 */
function formatPhone(phone: string): string {
  const digits = phone.replace(/[^\d+]/g, '');
  // +614xxxxxxxx → 04xx xxx xxx
  if (digits.startsWith('+614') && digits.length === 12) {
    const rest = digits.slice(3); // 4xx xxx xxx (no leading 0)
    return `0${rest.slice(0, 3)} ${rest.slice(3, 6)} ${rest.slice(6)}`;
  }
  if (digits.startsWith('04') && digits.length === 10) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  return phone;
}

// Silence the unused-import warning — we keep AdminCreateBookingApiError
// re-exported through the page module's lazy boundary; the SignInPrompt
// path doesn't currently invoke it but it's the right error type for
// any future surface that needs to translate API codes here.
export type _KeepApiErrorRef = AdminCreateBookingApiError;
